import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ButtonToolbar } from 'react-bootstrap';
import DatePicker from "react-datepicker";

import '../../styles/Admin.css';
import "react-datepicker/dist/react-datepicker.css";

import { AdminMenuItem } from './AdminMenuItem';
import { AdminQuestion } from './AdminQuestion';
import { AdminDropdown } from './AdminDropdown';
import { TrashIcon } from './Icons/TrashIcon';
import { CreateIcon } from './Icons/CreateIcon';
import { MenuForm } from './Forms/MenuForm';
import { QuestionForm } from './Forms/QuestionForm';
import { AnswerForm } from './Forms/AnswerForm';
import { createAnswer, createMenuItem, createQuestion, deleteMenuItem, deleteQuestion, getAllAnswersOnQuestionAndLanguageID, getAllCountriesOnLanguageID, getAllLanguages, getAllLanguagesOnLanguageID, getChildren, getMenus, getQuestion, getQuestionAndLanguageOnID, getQuestionTypeOnQuestionID } from '../../data-sources/api';

/**
 * AdminView component manages the admin interface, including fetching and displaying menus,
 * questions, answers, and handling form visibility and state updates.
 * 
 * @returns {JSX.Element} The admin view component.
 */
export function AdminView() {
    
    const [state, setState] = useState({
        questionToShow: 1000,
        answerToShow: 1000,
        shownDropdownId: null,
        displayLanguage: "Svenska",
        LanguageID: 1,
        displayHeader: "Sadima",
        MenuItems: [],
        SwedishQuestionItems: [],
        Languages: [],
        TranslationToShow: [],
        QuestionType: "PXQEmpty",
        ChildrenToChosenMenu: [],
        SweAnswerItems: [],
        ArabAnswerItems: [],
        AnswerItems: [],
        AudioToBePlayed: new Audio(""),
        AudioHasEnded: false,
        VideoToBePlayed: "",
        startDate: new Date(),
        loggedIn: localStorage.getItem('userAuthenticated'),
        showCreateMenuForm: false,
        showCreateQuestionForm: false,
        showCreateAnswerForm: false,
        parentID: null,
        someState: 1,
        SweCountries: [],
        ArabCountries: [],
        SweSpokenLanguages: [],
        ArabSpokenLanguages: [],
    });

    /**
     * Fetches menu items and languages on component mount.
     */
    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Updates audio and video sources when TranslationToShow state changes.
     */
    useEffect(() => {
        if (state.TranslationToShow.length > 0) {
            // AUDIO 
            state.AudioToBePlayed.src = state.TranslationToShow[0].audioUrl;

            if (state.QuestionType !== "PXQVideo") {
                const audio = document.getElementById("audio");
                audio.src = state.TranslationToShow[0].audioUrl;

                const audioPromise = audio.play();
                if (audioPromise !== undefined) {
                    audioPromise.then(() => {
                        console.log("audio played auto");
                    }).catch(error => {
                        console.log("didn't play audio");
                    });
                }

                audio.onended = () => {
                    setState(prevState => ({
                        ...prevState,
                        AudioHasEnded: true,
                        AudioToBePlayed: new Audio()
                    }));
                };
            } else {
                setState(prevState => ({
                    ...prevState,
                    VideoToBePlayed: state.TranslationToShow[0].videoUrl
                }));

                const video = document.getElementById("video");
                video.src = state.TranslationToShow[0].videoUrl;
            }
        }
    }, [state.TranslationToShow]);

    /**
     * Fetches initial data including menus, languages, and countries.
     */
    const fetchData = async () => {
        try {
            const [
                menus,
                languages,
                sweCountries,
                arabCountries,
                sweSpokenLanguages,
                arabSpokenLanguages
            ] = await Promise.all([
                getMenus(),
                getAllLanguages(),
                getAllCountriesOnLanguageID(1),
                getAllCountriesOnLanguageID(2),
                getAllLanguagesOnLanguageID(1),
                getAllLanguagesOnLanguageID(2)
            ]);

            setState(prevState => ({
                ...prevState,
                MenuItems: menus.data,
                Languages: languages.data,
                SweCountries: sweCountries.data,
                ArabCountries: arabCountries.data,
                SweSpokenLanguages: sweSpokenLanguages.data,
                ArabSpokenLanguages: arabSpokenLanguages.data
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    /**
     * Fetches menu items.
     */
    const fetchMenus = async () => {
        try {
            const response = await getMenus();
            setState(prevState => ({
                ...prevState,
                MenuItems: response.data
            }));
        } catch (error) {
            console.error("Error fetching question:", error);
        }
    }

    /**
     * Fetches question and language data based on statement ID and language ID.
     * 
     * @param {number} statementID - The ID of the statement.
     * @param {number} languageID - The ID of the language.
     */
    const fetchQuestionAndLanguageOnID = async (statementID, languageID) => {
        try {
            const response = await getQuestionAndLanguageOnID(statementID, languageID);

            setState(prevState => ({
                ...prevState,
                TranslationToShow: response.data
            }));

        } catch (error) {
            console.error("Error fetching question:", error);
        }
    }

    /**
    * Fetches questions based on submenu ID.
    * 
    * @param {number} subMenuId - The ID of the submenu.
    */
    const fetchQuestions = async (subMenuId) => {
        try {
            const response = await getQuestion(subMenuId);
            setState(prevState => ({
                ...prevState,
                SwedishQuestionItems: response.data
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    /**
     * Fetches children items based on submenu ID.
     * 
     * @param {number} subMenuId - The ID of the submenu.
     */
    const fetchChildren = async (subMenuId) => {
        try {
            const response = await getChildren(subMenuId);
            setState(prevState => ({
                ...prevState,
                ChildrenToChosenMenu: response.data
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    /**
     * Fetches question type based on submenu ID.
     * 
     * @param {number} subMenuId - The ID of the submenu.
     */
    const fetchQuestionType = async (subMenuId) => {
        try {
            const response = await getQuestionTypeOnQuestionID(subMenuId);
            setState(prevState => ({
                ...prevState,
                QuestionType: response.data
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    /**
     * Fetches answers based on statement ID and language ID.
     * 
     * @param {number} statementID - The ID of the statement.
     * @param {number} languageID - The ID of the language.
     * @returns {Promise<Array>} The fetched answers.
     */
    const fetchAnswers = async (statementID, languageID) => {
        try {
            const response = await getAllAnswersOnQuestionAndLanguageID(statementID, languageID);
            return response.data;
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };


    /**
     * Handles language change and updates the state to reflect the selected language.
     */
    const handleLanguageChange = async () => {
        const languages = state.Languages;
        const currentLanguageIndex = languages.findIndex(lang => lang.id === state.LanguageID);
        const nextLanguageIndex = (currentLanguageIndex + 1) % languages.length;

        setState(prevState => ({
            ...prevState,
            displayLanguage: languages[nextLanguageIndex].languageText,
            LanguageID: languages[nextLanguageIndex].id
        }));

        fetchQuestionAndLanguageOnID(state.answerToShow, languages[nextLanguageIndex].id);
    };

    /**
     * Handles answer change based on the selected ID.
     */
    const handleAnswerChange = async (id, audioUrl, videoUrl) => {
        state.AudioToBePlayed.pause();

        fetchQuestionType(id);

        setState(prevState => ({
            ...prevState,
            answerToShow: id,
            AudioToBePlayed: new Audio(),
            AudioHasEnded: false
        }));

        fetchQuestionAndLanguageOnID(id, state.LanguageID);

        const sweAnswers = await fetchAnswers(id, 1);
        const arabAnswers = await fetchAnswers(id, 2);

        const temp = sweAnswers.map((swe, index) => ({
            swe,
            arab: arabAnswers[index]
        }));

        setState(prevState => ({
            ...prevState,
            SweAnswerItems: sweAnswers,
            ArabAnswerItems: arabAnswers,
            AnswerItems: temp
        }));
    };

    /**
     *  Handles menu change based on the selected ID and updates the displayed question and header.
     */
    const handleMenuChange = async (id, menuText) => {
        if (document.getElementById(state.questionToShow) !== null) {
            document.getElementById(state.questionToShow).style.backgroundColor = 'white';
            document.getElementById(state.questionToShow).style.color = 'black';
        }

        setState(prevState => ({
            ...prevState,
            questionToShow: id,
            parentID: id,
            answerToShow: 1000,
            displayHeader: menuText,
            TranslationToShow: [],
            QuestionType: "",
            AnswerItems: [],
            ChildrenToChosenMenu: []
        }));

        fetchQuestions(id);
        fetchChildren(id);

        if (state.ChildrenToChosenMenu.length !== 0) {
            setState(prevState => ({
                ...prevState,
                dropDownParent: id
            }));
        }

        document.getElementById(id).style.backgroundColor = 'black';
        document.getElementById(id).style.color = 'white';
    };

    /**
     * Handles dropdown selection and updates the state to show the selected dropdown's children.
     */
    const handleDropdownSelection = (id) => {
        setState(prevState => ({
            ...prevState,
            shownDropdownId: id,
            answerToShow: 1000,
            TranslationToShow: [],
            AnswerItems: [],
            parentID: id
        }));
    };

    /**
     * Sets states for creating a menu and opens the menu form.
     */
    const handleCreateMenu = (id) => {
        setState(prevState => ({
            ...prevState,
            parentID: id,
            showCreateMenuForm: true,
            showCreateQuestionForm: false,
            showCreateAnswerForm: false
        }));
    };

    /**
     * Handles deletion of the selected menu item, and fetches updated menus and children to reflect changes on the page.
     */
    const handleMenuDelete = async (id) => {
        try {
            const response = await deleteMenuItem(id);
            console.log(response);
            console.log("Menu item deleted!")
        } catch (error) {
            console.error("Error deleting data:", error);
        }

        setState(prevState => ({
            ...prevState,
            someState: 5
        }));

        fetchChildren(state.questionToShow);
        fetchMenus();
    };

    /**
     * Sets states for creating a question and opens the question form.
     */
    const handleQuestionCreate = (id) => {
        setState(prevState => ({
            ...prevState,
            parentID: id,
            showCreateQuestionForm: true,
            showCreateMenuForm: false,
            showCreateAnswerForm: false
        }));

        // This line is not necessary in the hooks version as the state update is handled by setState
        // prevState.showCreateQuestionForm = true; 
    };

    /**
     * Sets states for creating an answer and opens the answer form.
     */
    const handleAnswerCreate = (id) => {
        setState(prevState => ({
            ...prevState,
            parentID: id,
            showCreateAnswerForm: true,
            showCreateQuestionForm: false,
            showCreateMenuForm: false
        }));
    };

    /**
     * Handles deletion of the selected question, and fetches updated questions and children to reflect changes on the page.
     */
    const handleQuestionDelete = async (id) => {
        try {
            const response = await deleteQuestion(id);
            console.log(response);
            console.log("Question deleted!")
        } catch (error) {
            console.error("Error deleting data:", error);
        }

        setState(prevState => ({
            ...prevState,
            ChildrenToChosenMenu: []
        }));

        fetchChildren(state.questionToShow);
        fetchQuestions(state.questionToShow);
    };

    /**
     * Handles creation of a new menu item, and fetches updated menus and children to reflect changes on the page.
     */
    const handleMenuSubmit = async (data, id) => {
        try {
            const response = await createMenuItem(data, id);
            console.log("Menu item created!")
        } catch (error) {
            console.error("Error fetching data:", error);
        }

        setState(prevState => ({
            ...prevState,
            showCreateMenuForm: false,
            ChildrenToChosenMenu: []
        }));

        fetchChildren(state.questionToShow);
        fetchMenus();
    };

    /**
     * Handles creation of a new question, and fetches updated questions and children to reflect changes on the page.
     */
    const handleQuestionSubmit = async (data, itemID, questionType) => {
        try {
            const response = await createQuestion(data, itemID, questionType);
            console.log(response);
            console.log("Question created!")
        } catch (error) {
            console.error("Error creating data:", error);
        }

        setState(prevState => ({
            ...prevState,
            showCreateQuestionForm: false,
            ChildrenToChosenMenu: []
        }));

        fetchChildren(state.questionToShow);
        fetchQuestions(state.parentID);
    };

    /**
     * Handles creation of a new answer to a question, and fetches updated answers to reflect changes on the page. 
     */
    const handleAnswerSubmit = async (data, id) => {
        try {
            const response = await createAnswer(data, id);
            console.log(response);
            console.log("Question created!")
        } catch (error) {
            console.error("Error creating data:", error);
        }

        setState(prevState => ({
            ...prevState,
            showCreateAnswerForm: false
        }));

        const sweAnswers = await fetchAnswers(id, 1);
        const arabAnswers = await fetchAnswers(id, 2);

        const temp = sweAnswers.map((swe, index) => ({
            swe,
            arab: arabAnswers[index]
        }));

        setState(prevState => ({
            ...prevState,
            SweAnswerItems: sweAnswers,
            ArabAnswerItems: arabAnswers,
            AnswerItems: temp
        }));
    };


    /**
     * Logs out the user by updating the local storage and state.
     */
    const handleLogout = () => {
        localStorage.setItem('userAuthenticated', 'false');

        setState(prevState => ({
            ...prevState,
            loggedIn: false
        }));
    };

    return (
        <div className="sadima">
            {state.showCreateMenuForm ? <MenuForm itemID={state.parentID} onSubmit={handleMenuSubmit} /> : <></>}
            {state.showCreateQuestionForm ? <QuestionForm itemID={state.parentID} languages={state.Languages} onSubmit={handleQuestionSubmit} /> : <></>}
            {state.showCreateAnswerForm ? <AnswerForm itemID={state.answerToShow} languages={state.Languages} onSubmit={handleAnswerSubmit} /> : <></>}

            <div>
                {/* HEADER */}
                <div className="top-header small-height">
                    <div className="top-left-header">
                        <h1>ADMIN</h1>
                        <button type="button" className="btn btn-default btn-sm" onClick={() => handleCreateMenu(null)} title="Skapa en Huvudmeny">
                            <CreateIcon />
                            <audio id="audio" className="audio-container" src="" />
                        </button>
                    </div>
                    <div className="top-right-header">
                        <h1 className="top-right-header-text">
                            {state.displayHeader}
                        </h1>
                        <div className="top-right-header-buttons">
                            {state.questionToShow !== 1000 &&
                                <button type="button" className="btn btn-default btn-sm" onClick={() => handleCreateMenu(state.questionToShow)} title="Skapa en dropdown meny">
                                    <CreateIcon />
                                </button>
                            }
                            <Button className="multichoice-btn" variant="outline-secondary" size="sm" onClick={handleLogout}>Logga ut</Button>
                        </div>
                    </div>
                </div>

                {/* MENU */}
                <div id="menu" className="admin-menu half-window-top">
                    <div className="choose-language-box lnu-background">
                        <h2>Patientens språk</h2>
                        <div className="choose-language-text" onClick={handleLanguageChange}>{state.displayLanguage} </div>
                    </div>

                    {state.MenuItems.map((props) => (
                        <div>
                            <div className="menu-header static-statements grey-background" >
                                <h2>{props.menuText}</h2>
                                <div className="menu-buttons">
                                    <button type="button" className="btn btn-default btn-sm" onClick={() => handleCreateMenu(props.id)} title="Skapa undermeny">
                                        <CreateIcon />
                                    </button>
                                    <button type="button" className="btn btn-default btn-sm" onClick={() => { if (window.confirm("Är du säker på att du vill ta bort menyn och allt under denna?")) handleMenuDelete(props.id) }} title="Ta bort huvudmeny">
                                        <TrashIcon />
                                    </button>
                                </div>
                            </div>

                            {props.menuChildren && props.menuChildren.map((props) => (
                                <AdminMenuItem
                                    itemText={props.menuText}
                                    itemID={props.id}
                                    onMenuChange={handleMenuChange}
                                    onDelete={handleMenuDelete}
                                    onCreate={handleQuestionCreate}
                                ></AdminMenuItem>
                            ))}
                        </div>
                    ))}
                </div>

                {/* MAIN CONTENT */}
                <div id="main" className="admin-main half-window-bottom">

                    {/* DROPDOWNS */}
                    <div className="row-5 half-window make-scrollable goback">
                        {state.ChildrenToChosenMenu.length !== 0 ?
                            <div>
                                {state.ChildrenToChosenMenu.map((props) => (
                                    <AdminDropdown
                                        shownDropdownId={state.shownDropdownId}
                                        handleDropdownSelection={handleDropdownSelection}
                                        itemText={props.menuText}
                                        itemID={props.id}
                                        onAnswerChange={handleAnswerChange}
                                        onCreate={handleQuestionCreate}
                                        onDelete={handleMenuDelete}
                                        onQuestionDelete={handleQuestionDelete}
                                    ></AdminDropdown>
                                ))}
                            </div>
                            :
                            <div>
                                {state.SwedishQuestionItems.map((props) => (
                                    <AdminQuestion
                                        itemID={props.id}
                                        question={props.textUrl}
                                        audio={props.audioUrl}
                                        video={props.videoUrl}
                                        onAnswerChange={handleAnswerChange}
                                        onDelete={handleQuestionDelete}
                                        onCreate={handleAnswerCreate}
                                    ></AdminQuestion>
                                ))}

                            </div>}
                    </div>

                    {/* ANSWERS */}
                    <div className={`tranwindow row half-window make-scrollable ${(state.QuestionType === "PXQVideo") && 'content-center'}`}>

                        { // If the statement's main content is a video
                            state.QuestionType === "PXQVideo" &&
                            <div>
                                <video id="video" controls autoPlay className="video-container" src="" >
                                </video>
                            </div>
                        }

                        { // If the question should be answered with plain text
                            (state.QuestionType === "PXQLetters" || state.QuestionType === "PXQNumbers" || state.QuestionType === "PXQYears") &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <input className="inputfield" autoFocus type="text" />
                                }
                            </div>
                        }

                        { // If the question should be answered with yes or no answers
                            state.QuestionType === "PXQYesNo" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <ButtonToolbar className="yesno">
                                        <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-success" size="lg"> Ja </Button>
                                        <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-danger" size="lg"> Nej </Button>
                                    </ButtonToolbar>
                                }
                            </div>
                        }

                        { // If the question should be answered with a date
                            state.QuestionType === "PXQCalendar" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <DatePicker selected={state.startDate} onChange={date => setState({ startDate: date })} />
                                }
                            </div>
                        }

                        { // If the question should be answered with a country
                            state.QuestionType === "PXQCountries" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <div>
                                        {state.LanguageID === 1 ?
                                            <select className="select-css">
                                                {state.SweCountries.map((props) => (
                                                    <option value={props.countryName}>{props.countryName}</option>
                                                ))}
                                            </select>
                                            :
                                            <select className="select-css">
                                                {state.ArabCountries.map((props) => (
                                                    <option value={props.countryName}>{props.countryName}</option>
                                                ))}
                                            </select>
                                        }
                                    </div>
                                }
                            </div>
                        }

                        { // If the question should be answered with a language
                            state.QuestionType === "PXQLanguages" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <div>
                                        {state.LanguageID === 1 ?
                                            <select className="select-css">
                                                {state.SweSpokenLanguages.map((props) => (
                                                    <option value={props.spokenLanguageText}>{props.spokenLanguageText}</option>
                                                ))}
                                            </select>
                                            :
                                            <select className="select-css">
                                                {state.ArabSpokenLanguages.map((props) => (
                                                    <option value={props.spokenLanguageText}>{props.spokenLanguageText}</option>
                                                ))}
                                            </select>
                                        }
                                    </div>
                                }
                            </div>
                        }

                        { // If the question should be answered with yes or no answer, and then with a country
                            state.QuestionType === "PXQYesNoCountries" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <div>
                                        {state.LanguageID === 1 ?
                                            <div>
                                                <ButtonToolbar className="yesno">
                                                    <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-success" size="lg"> Ja </Button>
                                                    <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-danger" size="lg"> Nej </Button>
                                                </ButtonToolbar>
                                                <select className="select-css">
                                                    {state.SweCountries.map((props) => (
                                                        <option value={props.countryName}>{props.countryName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            :
                                            <select className="select-css">
                                                {state.ArabCountries.map((props) => (
                                                    <option value={props.countryName}>{props.countryName}</option>
                                                ))}
                                            </select>
                                        }
                                    </div>
                                }
                            </div>
                        }

                        { // If the question should be answered with multi choice buttons
                            state.QuestionType === "PXQMultiButton" &&
                            <div className="col-6 answerbox">
                                {state.AnswerItems.length === 0 ?
                                    <div className="create-answer-container">
                                        <button type="button" className="btn btn-default btn-sm" style={{ float: "right" }} onClick={handleAnswerCreate} title="Skapa svarsalternativ">
                                            <CreateIcon />
                                        </button>
                                    </div>
                                    :
                                    <div>
                                        {state.AudioHasEnded ?
                                            <div className="answer-container-show">
                                                <div className="create-answer-container">
                                                    <button type="button" className="btn btn-default btn-sm" style={{ float: "right" }} onClick={handleAnswerCreate} title="Lägg till svarsalternativ">
                                                        <CreateIcon />
                                                    </button>
                                                </div>
                                                <div className="make-scrollable">
                                                    {state.AnswerItems.map((props) => (
                                                        <Button className="multichoice-btn" variant="outline-secondary" size="lg"> {props.swe.answerText} {props.arab.answerText} </Button>
                                                    ))}
                                                </div>
                                            </div>
                                            :
                                            <div className="col-6 answerbox"></div>
                                        }
                                    </div>}
                            </div>
                        }

                        { // If the question should be answered with multi select buttons, i.e. multi choice buttons where multiple choices can be selected
                            state.QuestionType === "PXQMultiSelect" &&
                            <div className="col-6 answerbox">
                                {state.AnswerItems.length === 0 ?
                                    <div className="create-answer-container">
                                        <button type="button" className="btn btn-default btn-sm" style={{ float: "right" }} onClick={handleAnswerCreate} title="Skapa svarsalternativ">
                                            <CreateIcon />
                                        </button>
                                    </div>
                                    :
                                    <div>
                                        {state.AudioHasEnded &&
                                            <div className="answer-container-show">
                                                <div className="create-answer-container">
                                                    <button type="button" className="btn btn-default btn-sm" style={{ float: "right" }} onClick={handleAnswerCreate} title="Lägg till svarsalternativ">
                                                        <CreateIcon />
                                                    </button>
                                                </div>
                                                <div className="make-scrollable">
                                                    {state.AnswerItems.map((props) => (
                                                        <Button className="multichoice-btn" variant="outline-secondary" size="lg"> {props.swe.answerText} {props.arab.answerText}</Button>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                    </div>}
                            </div>
                        }

                        { // If the question should be answered with a yes or no answer, then with multi choice buttons 
                            state.QuestionType === "PXQYesNoMulti" &&
                            <div className="col-6 answerbox">
                                {state.AudioHasEnded &&
                                    <div>
                                        <ButtonToolbar className="yesno">
                                            <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-success" size="lg"> Ja </Button>
                                            <Button className="yesno d-flex justify-content-center d-md-table mx-auto" variant="outline-danger" size="lg"> Nej </Button>
                                        </ButtonToolbar>
                                        {state.AnswerItems.map((props) => (
                                            <Button className="multichoice-btn" variant="outline-secondary" size="lg" onClick={() => handlePlaySound(props.swe.answerAudio, props.arab.answerAudio)} ><div><div className="col">{props.swe.answerText}</div> <div className="col">{props.arab.answerText}</div> </div></Button>
                                        ))}
                                    </div>
                                }
                            </div>
                        }

                        { // If the statement's main content is a question
                            state.QuestionType !== "PXQVideo" &&
                            <div className="col-6 answerbox">
                                {state.TranslationToShow.map((props) => (
                                    <div className="answer">
                                        {props.textUrl}
                                    </div>
                                ))
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}
